import { JobsList, JobsListFilter } from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import React, { useContext, useMemo, useState } from 'react';
import compact from 'just-compact';
import { useQuery } from 'urql';
import flush from 'just-flush';
import { FindAllJobSummariesQueryDocument, FindRoleExperienceOrderQueryDocument, type JobSummaryFieldsFragmentFragment } from '@/gql/client/graphql';
import { useFragment } from '@/gql';
import { JobSummaryFieldsFragment, TermNodeFieldsFragment } from '@/gql/documents/fragments';
import { LinesContext } from '@/components/LinesContext';
import { acfFirst, acfNode } from '@/utils';

function getOfficeCountry(job: JobSummaryFieldsFragmentFragment) {
  return acfFirst(acfNode(job.cptJobFields?.office, 'Office')?.cptOfficeCustomFields?.country);
}

type FiltersMap = {
  experience?: string
  location?: string
};

type FiltersConfig = {
  experience: JobsListFilter<'experience', string>
  location: JobsListFilter<'location', string>
};

type Props = {
  data: {
    showFilters?: boolean
  }
};

export default function NvisoJobsList({
  data: { showFilters },
}: Props) {
  const { enableLines } = useContext(LinesContext);
  const { t } = useTranslation();
  const [{ data: roleExperienceOrderQueryResult }] = useQuery({
    query: FindRoleExperienceOrderQueryDocument,
  });
  const [{ data: jobSummariesQueryResult }] = useQuery({
    query: FindAllJobSummariesQueryDocument,
  });
  const jobsFields = useFragment(JobSummaryFieldsFragment, flush(jobSummariesQueryResult?.jobs?.nodes ?? []));

  const [filters, setFilters] = useState<Partial<{
    [K in keyof FiltersMap]: FiltersMap[K][]
  }>>({});
  const experienceOptions = useMemo(() => {
    const options = new Map<string, string>();
    jobsFields.forEach((job) => {
      const experienceTerms = useFragment(TermNodeFieldsFragment, flush(job.roleExperience?.nodes ?? []));
      experienceTerms.forEach((term) => {
        if (!options.has(term.id)) {
          options.set(term.id, term.name ?? '');
        }
      });
    });
    const idOrder = compact(roleExperienceOrderQueryResult?.roleExperience?.nodes?.map((entry) => entry?.id) ?? []);
    const optionsList = Array.from(options.entries()).map(([value, label]) => ({
      value, label,
    }));
    optionsList.sort((a, b) => {
      const aIndexOf = idOrder.indexOf(a.value);
      const bIndexOf = idOrder.indexOf(b.value);
      return aIndexOf < bIndexOf ? -1 : 1;
    });
    return optionsList;
  }, [jobsFields]);
  const locationOptions = useMemo(() => {
    const options = new Map<string, {
      label: string
      value: string
    }>([]);
    jobsFields.forEach((job) => {
      const country = getOfficeCountry(job);
      if (country && !options.has(country)) {
        options.set(country, {
          value: country,
          label: t(`countries:${country}`),
        });
      }
    });
    return Array.from(options.values());
  }, [jobsFields]);
  const filtersConfig = useMemo<FiltersConfig[keyof FiltersConfig][]>(() => {
    if (showFilters) {
      return [
        {
          label: t('global:job-filters.select-by-experience'),
          name: 'experience',
          options: experienceOptions,
        },
        {
          label: t('global:job-filters.select-by-location'),
          name: 'location',
          options: locationOptions,
        },
      ];
    }
    return [];
  }, [showFilters, experienceOptions]);

  const jobEntries = useMemo(() => {
    const filteredJobs = jobsFields.filter((job) => (
      (!filters.experience?.length || job.roleExperience?.nodes?.some((exp) => {
        const fields = useFragment(TermNodeFieldsFragment, exp);
        return fields && filters.experience?.includes(fields.id);
      }))
      && (!filters.location?.length || (() => {
        const country = getOfficeCountry(job);
        return !!country && filters.location.includes(country);
      })())
    ));
    return filteredJobs.map((job) => {
      const country = getOfficeCountry(job);
      return {
        key: job.id,
        title: job.title ?? '',
        label: job.cptJobFields?.isRemote ? t('global:remote') : (
          country ? t(`countries:${country}`) : undefined
        ),
        callToAction: {
          href: job.uri ?? '/',
        },
      };
    });
  }, [jobsFields, filters]);

  return (
    <JobsList<FiltersMap>
      enableLines={enableLines}
      filtersConfig={filtersConfig}
      filters={filters}
      jobs={jobEntries}
      onFiltersChange={setFilters}
    />
  );
}
